import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const WestminsterInvestmentPropertiesPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&propertySubType=Multi-Family&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching investment properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Westminster Investment Properties | Multi-Family & Rentals | Truc Doan</title>
        <meta name="description" content="Find Westminster CA investment properties including multi-family homes, rental units, and commercial properties near Little Saigon. Cap rates 4-6%, high rental demand. Truc Doan, your investment property specialist." />
        <meta name="keywords" content="Westminster investment properties, multi-family Westminster CA, rental properties Westminster, Little Saigon commercial real estate, duplex Westminster, triplex Westminster, cap rate Westminster, ROI rental property Orange County, Truc Doan investor agent" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/westminster-investment-properties`} />
        <meta property="og:title" content="Westminster Investment Properties | Multi-Family & Rentals | Truc Doan" />
        <meta property="og:description" content="Investment properties in Westminster CA near Little Saigon. Multi-family, rentals, commercial. Cap rates 4-6%. Expert guidance from Truc Doan." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/westminster-investment-properties`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/westminster-investment-properties`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan specializes in Westminster CA investment properties including multi-family homes, rental units, and commercial real estate near Little Saigon.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Westminster Investment Properties – Multi-Family & Rental Opportunities</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Discover high-yield investment properties in Westminster and Little Saigon with expert guidance from Truc Doan.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Invest in Westminster Real Estate?</h2>
          <p>Westminster, home to Little Saigon – the largest Vietnamese-American commercial district in the U.S. – offers exceptional investment opportunities. The area's thriving economy, growing population, and cultural significance drive consistent rental demand and property appreciation.</p>
          <p>Multi-family properties near Bolsa Avenue and the Little Saigon corridor benefit from high foot traffic, strong tenant pools, and proximity to restaurants, shops, and cultural centers that attract residents year-round.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Investment Opportunities</h2>
          <p>Westminster offers diverse investment options for both seasoned investors and first-time buyers looking to build wealth through real estate.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Multi-Family Properties</h3>
              <p>Duplexes, triplexes, and fourplexes in high-demand neighborhoods with strong rental history and low vacancy rates near Little Saigon.</p>
            </div>
            <div className="amenity">
              <h3>Commercial Mixed-Use</h3>
              <p>Retail/residential mixed-use properties along Bolsa Avenue and Westminster Boulevard offering dual income streams from commercial tenants and residential units.</p>
            </div>
            <div className="amenity">
              <h3>Rental Income Potential</h3>
              <p>High rental demand from the growing Vietnamese-American community, young professionals, and families seeking affordable Orange County living near cultural amenities.</p>
            </div>
            <div className="amenity">
              <h3>Cap Rate & ROI Analysis</h3>
              <p>Current cap rates of 4-6% in Westminster, with value-add opportunities that can push returns higher through renovations and improved property management.</p>
            </div>
            <div className="amenity">
              <h3>1031 Exchange Guidance</h3>
              <p>Expert assistance with 1031 exchanges to defer capital gains and upgrade your investment portfolio with Westminster properties.</p>
            </div>
            <div className="amenity">
              <h3>Property Management Connections</h3>
              <p>Network of trusted bilingual property managers familiar with Westminster's tenant demographics and local regulations.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Westminster Investment Market Snapshot</h2>
          <p>Westminster's investment market remains strong with consistent appreciation and high rental demand driven by the Little Saigon economy.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Avg Multi-Family Price</td><td>~$1,100,000</td></tr>
              <tr><td>Cap Rate Range</td><td>4–6%</td></tr>
              <tr><td>Rental Demand</td><td>High</td></tr>
              <tr><td>Vacancy Rate</td><td>Low (2-4%)</td></tr>
              <tr><td>Avg Rent (2BR)</td><td>$2,200–$2,600/mo</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Available Investment Properties in Westminster</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Browse all available Westminster investment listings by clicking "Search Listings" below.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Invest in Westminster Real Estate?</h2>
          <p>Contact Truc Doan today for a free investment consultation and discover the best multi-family and rental opportunities in Westminster.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Westminster Investment Property Inquiry" className="cta-button primary">Email for Investment Analysis</a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">Call +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Investment Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default WestminsterInvestmentPropertiesPage;
